// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

// HW.2/HW.3 (COB-322/323): typed errors + app-name guard for the Ledger
// device stack. Split out of ledger-agent.ts at HW.3 with ZERO runtime
// imports, because the SPA's session adapter (main bundle-adjacent, unit
// tested in jsdom) needs the error classes without evaluating
// ledger-bitcoin's module graph — the classes are the shared surface
// between the device stack and the UI that renders its failures.

import type { NetworkName } from './crypto.js';

/**
 * Thrown by `KeyAgent` methods a hardware device cannot serve (yet).
 * `method` identifies the call; the message names the rung that lands the
 * capability, so a UI hitting this in development knows what it is
 * waiting for rather than guessing at a generic failure.
 */
export class UnsupportedOnHardwareError extends Error {
  readonly method: string;

  constructor(method: string, detail: string) {
    super(`${method} is not available on a hardware device: ${detail}`);
    this.name = 'UnsupportedOnHardwareError';
    this.method = method;
  }
}

/**
 * Thrown when `signPsbt` finds no registered wallet-policy record for the
 * vault. The record is persisted by the HW.4 registration ceremony; without
 * it the device cannot evaluate the vault's policy — fail closed.
 */
export class PolicyNotRegisteredError extends Error {
  readonly vaultId: string;

  constructor(vaultId: string) {
    super(
      `No registered wallet policy for vault ${vaultId} — run the device ` +
        `registration ceremony before signing (fail closed)`,
    );
    this.name = 'PolicyNotRegisteredError';
    this.vaultId = vaultId;
  }
}

/**
 * COB-336: the device probe timed out, or the transport is wedged
 * (its internal busy lock is held by an exchange that never resolved) —
 * the device is locked, asleep, in use by another app, or on a half-dead
 * HID link. The guarded client that raised this is marked dead: every
 * later call fails with this same error, no APDU attempted, until the
 * caller reconnects a fresh device. Distinguished from
 * {@link DeviceDisconnectedError} by name + `instanceof`.
 */
export class DeviceUnresponsiveError extends Error {
  constructor() {
    super(
      'The device did not respond — it may be locked, disconnected, or busy ' +
        'with another app. Reconnect and unlock your Ledger, then try again.',
    );
    this.name = 'DeviceUnresponsiveError';
  }
}

/**
 * COB-336: the WebHID transport reported the device gone (a `disconnect`
 * event, or a disconnect thrown mid-exchange). The guarded client is
 * marked dead exactly as for {@link DeviceUnresponsiveError}. Kept a
 * distinct type so the SPA can say "reconnect" specifically.
 */
export class DeviceDisconnectedError extends Error {
  constructor() {
    super('Your Ledger was disconnected. Reconnect the device and try again.');
    this.name = 'DeviceDisconnectedError';
  }
}

/** Thrown at connect time when the open Ledger app doesn't match the network. */
export class WrongDeviceAppError extends Error {
  readonly appName: string;
  readonly expectedApp: string;

  constructor(appName: string, expectedApp: string) {
    super(
      `The Ledger has the "${appName}" app open but this network needs ` +
        `"${expectedApp}" — quit the current app and open "${expectedApp}" ` +
        `(install via Ledger Live → My Ledger if missing), then reconnect.`,
    );
    this.name = 'WrongDeviceAppError';
    this.appName = appName;
    this.expectedApp = expectedApp;
  }
}

/** Ledger app expected for a network: coin `1'` paths live in "Bitcoin Test". */
export function expectedDeviceApp(network: NetworkName): string {
  return network === 'mainnet' ? 'Bitcoin' : 'Bitcoin Test';
}

/**
 * Assert the open device app matches the network (spike gotcha: the
 * mainnet app returns xpub-versioned keys and rejects coin-1' policies
 * with a bare 0x6a80 long before anything readable happens).
 */
export function assertExpectedDeviceApp(appName: string, network: NetworkName): void {
  const expected = expectedDeviceApp(network);
  if (appName !== expected) {
    throw new WrongDeviceAppError(appName, expected);
  }
}
